import {
    Box,
    Button,
    Form,
    FormField,
    Layer,
    Notification,
    RangeInput,
    Text,
} from 'grommet';
import { Close, Edit, StatusCritical, StatusGood } from 'grommet-icons';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useRevalidator } from 'react-router-dom';
import { ContactService, Utils } from '@enrich/shared-lib';

export const ContactProfileFormModalButton = ({ contact }) => {
    const [showModal, setShowModal] = useState(false);

    const [showSuccessNotification, setShowSuccessNotification] =
        useState(false);

    const [failureNotification, setFailureNotification] = useState({
        isVisible: false,
        message: '',
    });

    const revalidator = useRevalidator();

    return (
        <>
            <Button
                primary
                label={<Edit color="white" size="medium" />}
                size="small"
                onClick={() => setShowModal(true)}
            />
            {showSuccessNotification && (
                <Notification
                    toast={{ autoClose: true, position: 'top' }}
                    status="info"
                    time={8000}
                    title="Contact updated successfully!"
                    icon={<StatusGood color="green" />}
                    onClose={() => setShowSuccessNotification(false)}
                />
            )}
            {failureNotification.isVisible && (
                <Notification
                    toast={{ autoClose: true, position: 'top' }}
                    status="info"
                    time={8000}
                    title="Failed to update contact!"
                    message={
                        failureNotification.message ||
                        'Please try again or contact support.'
                    }
                    icon={<StatusCritical color="red" />}
                    onClose={() =>
                        setFailureNotification({
                            isVisible: false,
                            message: '',
                        })
                    }
                />
            )}
            {showModal && (
                <ContactProfileFormModal
                    setShowModal={setShowModal}
                    contact={contact}
                    setShowSuccessNotification={setShowSuccessNotification}
                    setFailureNotification={setFailureNotification}
                    revalidate={revalidator.revalidate}
                />
            )}
        </>
    );
};

const ContactProfileFormModal = ({
    setShowModal,
    contact,
    setShowSuccessNotification,
    setFailureNotification,
    revalidate,
}) => {
    const defaultValues = {
        id: contact.id,
        communicationLevel: contact.attributes?.communicationLevel || 0,
    };

    const [value, setValue] = useState(defaultValues);
    const [buttonBusy, setButtonBusy] = useState(false);

    const handleSubmit = async () => {
        setButtonBusy(true);
        try {
            await new ContactService().updateContact(value);
            setShowSuccessNotification(true);
            setShowModal(false);
            revalidate();
        } catch (error) {
            console.log({ error });
            setFailureNotification({ isVisible: true, message: '' });
            setButtonBusy(false);
        }
    };

    return (
        <Layer
            position="center"
            background={{ color: 'enrich-green', opacity: 'medium' }}
            onEsc={() => setShowModal(false)}
            onClickOutside={() => setShowModal(false)}>
            <Box
                width="medium"
                direction="column"
                background="white"
                pad="medium"
                round="20px"
                border={{ color: 'enrich-green-light', size: '3px' }}>
                <Box fill="horizontal" direction="row" justify="end">
                    <Box
                        round={true}
                        onClick={() => setShowModal(false)}
                        cursor="pointer">
                        <Close color="enrich-red" />
                    </Box>
                </Box>
                <Form>
                    <FormField
                        label="Communication Level"
                        id="communicationLevelInput">
                        <Box direction="row-responsive" gap="large">
                            <Box>
                                <RangeInput
                                    id="communicationLevelInput"
                                    value={value.communicationLevel}
                                    min={0}
                                    max={3}
                                    onChange={(event) =>
                                        setValue({
                                            ...value,
                                            communicationLevel: Number(
                                                event.target.value,
                                            ),
                                        })
                                    }
                                />
                            </Box>
                            <Box>
                                <Text>
                                    {
                                        // eslint-disable-next-line max-len
                                        Utils.resolveContactCommunicationLevelDescription(
                                            value.communicationLevel,
                                        )
                                    }
                                </Text>
                            </Box>
                        </Box>
                    </FormField>
                    <Box direction="row-responsive" justify="center" gap="large">
                        <Button
                            primary
                            label="Update"
                            disabled={buttonBusy}
                            onClick={() => handleSubmit()}
                        />
                        <Button
                            secondary
                            label="Close"
                            onClick={() => setShowModal(false)}
                            hoverIndicator={{ color: 'red' }}
                        />
                    </Box>
                </Form>
            </Box>
        </Layer>
    );
};

ContactProfileFormModalButton.propTypes = {
    contact: PropTypes.object,
};

ContactProfileFormModal.propTypes = {
    setShowModal: PropTypes.func,
    contact: PropTypes.object,
    setShowSuccessNotification: PropTypes.func,
    setFailureNotification: PropTypes.func,
    revalidate: PropTypes.func,
};
